/**
 * Widget V2 - InstallAppCard
 *
 * The one strip that walks a phone from the browser to notifications
 * (FEATURE_WIDGET_PWA_WEB_PUSH.md D5/D6). In a phone browser it offers the app for
 * the Home Screen: one tap on Android Chrome, the phone's own steps everywhere
 * else. Inside the installed app the same slot asks to turn notifications on; the
 * browser's permission prompt only appears after that tap, never on load.
 *
 * "Never show again" puts the install offer away for good and "Not now" snoozes the
 * notifications ask for two weeks. Renders nothing on a desktop, when the user is
 * signed out, or once the phone has the app and is on. No close button: every way
 * out is a remembered answer, so the strip never nags visit after visit.
 */

import React, { useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import AddToHomeScreenIcon from "@mui/icons-material/AddToHomeScreen";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import { useSnackbar } from "notistack";

import { useAuth } from "/imports/state/AuthContext";
import { useWidget } from "/imports/state/WidgetContext";
import { track } from "/imports/libs/analytics";
import {
  canPromptInstall,
  currentCardState,
  dismissInstall,
  installSteps,
  isPushSupported,
  markInstalled,
  onPushChange,
  promptInstall,
  snooze,
  turnOnPush
} from "/imports/libs/push";

const ICONS = {
  install: AddToHomeScreenIcon,
  ask: NotificationsActiveOutlinedIcon,
  denied: NotificationsOffOutlinedIcon
};

// Two lines at most: a title, and under it either one line of text or the way out.
const COPY = {
  install: { title: (club) => `Install the ${club} app`, body: null },
  ask: { title: "Turn on booking reminders and club offers", body: null },
  denied: { title: "Notifications are blocked", body: "Allow them for this app in your phone's settings." }
};

// Trainers never receive offers (D4): their ask only promises their own bookings.
const TRAINER_COPY = {
  ...COPY,
  ask: { title: "Turn on new-booking alerts", body: null }
};

const NOT_ELIGIBLE_MESSAGE = "Notifications are available once your account is linked to this club.";

// Two looks: "card" on a page (white, bordered), "hero" inside the home band
// (translucent white on the brand gradient, like the next-booking strip).
const LOOKS = {
  card: {
    box: {
      borderRadius: 2,
      border: 1,
      borderColor: (t) => alpha(t.palette.primary.main, 0.25),
      bgcolor: "background.paper",
      mb: 2
    },
    icon: { color: "primary.main" },
    body: { color: "text.secondary" },
    primary: { color: "common.white" },
    secondary: { color: "error.main", fontWeight: 600 }
  },
  hero: {
    box: {
      borderRadius: 3,
      color: "common.white",
      bgcolor: "rgba(255,255,255,0.16)",
      border: "1px solid rgba(255,255,255,0.28)",
      backdropFilter: "blur(4px)"
    },
    icon: { color: "common.white" },
    body: { color: "rgba(255,255,255,0.85)" },
    primary: {
      bgcolor: "common.white",
      color: "primary.dark",
      fontWeight: 700,
      boxShadow: "none",
      "&:hover": { bgcolor: "rgba(255,255,255,0.88)", boxShadow: "none" }
    },
    // Red reads as an error on the green band: white, underlined, so it still
    // looks like a link.
    secondary: { color: "rgba(255,255,255,0.9)", textDecorationColor: "rgba(255,255,255,0.5)", fontWeight: 600 }
  }
};

function InstallAppCard({ audience = "client", variant = "card", sx }) {
  const look = LOOKS[variant] || LOOKS.card;
  const { slug, widget, saas, push } = useWidget();
  const { isAuthenticated } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const pushEnabled = !!push?.enabled && !!push?.publicKey;

  const [state, setState] = useState(() => (slug ? currentCardState(slug, { pushEnabled }) : "hidden"));
  const [showSteps, setShowSteps] = useState(false);
  const [busy, setBusy] = useState(false);

  // The same strip on the home band and the dashboard, and the install prompt that
  // Chrome hands over after load: every change is announced, so re-read on each.
  useEffect(() => {
    if (!slug) return undefined;
    const read = () => setState(currentCardState(slug, { pushEnabled }));
    read();
    return onPushChange(read);
  }, [slug, pushEnabled]);

  // One "offered" per screen the strip appears on, so the funnel has a top.
  useEffect(() => {
    if (isAuthenticated && slug && state === "install") track("app_install_offered", { placement: variant });
  }, [isAuthenticated, slug, state, variant]);

  if (!isAuthenticated || !slug || state === "hidden") return null;

  const clubName = saas?.name || widget?.name || "this club";
  const Icon = ICONS[state];
  const copy = (audience === "trainer" ? TRAINER_COPY : COPY)[state];
  const title = typeof copy.title === "function" ? copy.title(clubName) : copy.title;
  const copyBody = copy.body;
  const body = state === "install" && showSteps ? installSteps(navigator.userAgent || "", navigator.maxTouchPoints || 0) : copyBody;

  const hide = () => setState("hidden");

  // Chrome on Android opens its own install dialog; any other phone browser gets
  // the steps in the strip instead. A refused dialog can't be reopened, so the next
  // tap shows the steps too. Once installed, the same tap goes straight on to the
  // notifications permission: the browser and the installed app share it on
  // Android, so the app opens with notifications already on. If the permission
  // is refused or the prompt doesn't show, the installed app asks again itself.
  const handleInstall = async () => {
    track("app_install_tapped", { placement: variant, method: canPromptInstall() ? "dialog" : "steps" });
    if (!canPromptInstall()) {
      setShowSteps(true);
      return;
    }
    setBusy(true);
    try {
      const outcome = await promptInstall();
      if (outcome !== "accepted") return;
      markInstalled(slug);
      // Not "on your Home Screen": whether the icon lands there or only in the app
      // list is the launcher's setting (off by default on some Samsungs).
      let message = `The ${clubName} app is installed`;
      if (pushEnabled && isPushSupported()) {
        const { permission } = await turnOnPush({ slug, publicKey: push.publicKey }).catch(() => ({ permission: "default" }));
        if (permission === "granted") {
          track("notifications_turned_on", { placement: variant, with_install: true });
          message = `The ${clubName} app is installed and notifications are on`;
        } else if (permission === "denied") {
          track("notifications_denied", { placement: variant, with_install: true });
        }
      }
      enqueueSnackbar(message, { variant: "success" });
      hide();
    } finally {
      setBusy(false);
    }
  };

  const handleNever = () => {
    track("app_install_dismissed", { placement: variant });
    dismissInstall(slug);
    hide();
  };

  const handleNotNow = () => {
    snooze(slug);
    hide();
  };

  // After the manual steps: no browser can tell us the app was added (iOS Safari
  // and the installed app don't even share storage), so the client's "Done" is
  // what stops the browser from offering it again.
  const handleDone = () => {
    // No browser event for a manual add: the client's word is the count.
    track("app_installed", { method: "steps" });
    markInstalled(slug);
    hide();
  };

  // turnOnPush is called before anything is awaited: Safari only shows the
  // permission prompt from inside the tap itself.
  const handleTurnOn = async () => {
    setBusy(true);
    try {
      const { permission } = await turnOnPush({ slug, publicKey: push.publicKey });
      if (permission === "granted") {
        track("notifications_turned_on", { placement: variant });
        enqueueSnackbar("Notifications are on for this phone", { variant: "success" });
        hide();
      } else if (permission === "denied") {
        track("notifications_denied", { placement: variant });
        setState("denied");
      }
      // "default": the prompt was closed without an answer — leave the strip as it was.
    } catch (err) {
      enqueueSnackbar(err.code === "NOT_ELIGIBLE" ? NOT_ELIGIBLE_MESSAGE : err.message || "Notifications could not be turned on. Please try again.", {
        variant: "error"
      });
    } finally {
      setBusy(false);
    }
  };

  // One button and one quiet way out, per face. Once the steps are showing the
  // button becomes "Done".
  let primary;
  let secondary = null;
  if (state === "install") {
    primary = showSteps ? { label: "Done", onClick: handleDone } : { label: "Install", onClick: handleInstall };
    secondary = { label: "Never show again", onClick: handleNever };
  } else if (state === "ask") {
    primary = { label: "Turn on", onClick: handleTurnOn };
    secondary = { label: "Not now", onClick: handleNotNow };
  } else {
    primary = { label: "Got it", onClick: handleNotNow };
  }

  return (
    // The same shape as the next-booking strip above it: icon, two lines of text,
    // one button.
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.75, py: 1, ...look.box, ...sx }}>
      <Icon sx={{ ...look.icon, fontSize: "1.375rem", flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
        {body && <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.3, ...look.body }}>{body}</Typography>}
        {secondary && (
          <Link
            component="button"
            type="button"
            underline="always"
            disabled={busy}
            onClick={secondary.onClick}
            sx={{ display: "block", fontSize: "0.75rem", lineHeight: 1.3, mt: 0.375, ...look.secondary }}
          >
            {secondary.label}
          </Link>
        )}
      </Box>
      <Button
        variant="contained"
        size="small"
        disabled={busy}
        onClick={primary.onClick}
        sx={{
          textTransform: "none",
          flexShrink: 0,
          whiteSpace: "nowrap",
          borderRadius: 2,
          minHeight: 0,
          py: 0.375,
          px: 1.5,
          fontSize: "0.8rem",
          ...look.primary
        }}
      >
        {busy ? <CircularProgress size={16} color="inherit" /> : primary.label}
      </Button>
    </Box>
  );
}

export default InstallAppCard;
